import type { SelectOptionType } from '../../Controls/Select/types';
import type {
  FilterBarOperation,
  FilterChangeSource,
  FilterFieldPhase,
  FilterItemConfig,
  FilterItemType,
  FilterValues,
} from '../types';

// ---------- State shape ----------

export interface FieldState {
  phase: FilterFieldPhase;
  options: SelectOptionType[];
  value: unknown;
  userTouched: boolean;
  error: string | null;
  requestId: string | null;
}

export interface State {
  fields: Record<string, FieldState>;
  itemsByName: Record<string, FilterItemConfig>;
  itemsOrder: string[];
  visibleFields: string[];
  transactionOpen: boolean;
  transactionSource: FilterChangeSource;
  transactionTrigger: string | undefined;
  transactionOperation: FilterBarOperation | undefined;
  prevValuesAtTxnStart: FilterValues;
  partialErrors: string[];
  initialized: boolean;
  /**
   * Monotonic counter — incremented on every CASCADE_CLOSE.
   * The init sequencer uses this (rather than transactionOpen edge detection)
   * because React 18 batches multiple dispatches in one tick: a CASCADE_OPEN +
   * CASCADE_CLOSE in the same batch never produces a render with open=true.
   * The counter survives batching.
   */
  closedTxnCount: number;
  initEmitCount: number;
}

// ---------- Canonical empties ----------

export function canonicalEmpty(item: FilterItemConfig): unknown {
  switch (item.type) {
    case 'SELECT': return null;
    case 'SELECT_MULTI': return [];
    case 'TEXT_SEARCH': return '';
    case 'SEGMENT_TOGGLE': return null;
    case 'DATE_RANGE': return null;
    case 'NUMERIC_RANGE': return null;
  }
}

/**
 * Synchronous baseline target for `reset` / non-clearable `clear` paths:
 * static `defaultValue` if set, else `null` to signal "needs async resolution
 * via `getDefaultValue`." The orchestrator handles the async fallback.
 */
export function baselineTarget(item: FilterItemConfig): unknown {
  if (item.defaultValue !== undefined) return item.defaultValue;
  return null;
}

/** Option-backed types whose value is a single option value. */
const SCALAR_OPTION_TYPES: readonly FilterItemType[] = ['SELECT', 'SEGMENT_TOGGLE'];

/**
 * The option-backed family — SELECT, SEGMENT_TOGGLE (scalar) and SELECT_MULTI
 * (array) — shares one option domain, so a type flip inside the family
 * converts the value optimistically instead of purging: scalar ↔ scalar is
 * identity, scalar → array wraps (`'a'` → `['a']`), array → scalar keeps the
 * FIRST selection (deterministic; the change surfaces through the reconcile
 * transaction's onChange with true prevValues). The orchestrator re-validates
 * the converted value against freshly-read options, so an invalid conversion
 * self-heals through the normal stale-value flow. Every other type change has
 * no shared value domain and purges (plan: "different type → treat as
 * remove + add").
 */
export function convertOptionBackedValue(
  prevType: FilterItemType,
  nextType: FilterItemType,
  value: unknown,
): { converted: boolean; value?: unknown } {
  const prevScalar = SCALAR_OPTION_TYPES.includes(prevType);
  const nextScalar = SCALAR_OPTION_TYPES.includes(nextType);
  const prevMulti = prevType === 'SELECT_MULTI';
  const nextMulti = nextType === 'SELECT_MULTI';
  if ((!prevScalar && !prevMulti) || (!nextScalar && !nextMulti)) {
    return { converted: false };
  }
  if (prevScalar && nextScalar) {
    return { converted: true, value: value === undefined ? null : value };
  }
  if (prevScalar && nextMulti) {
    return { converted: true, value: value === null || value === undefined ? [] : [value] };
  }
  // prevMulti && nextScalar (multi → multi is the same type, never reaches here)
  const arr = Array.isArray(value) ? value : [];
  return { converted: true, value: arr.length > 0 ? arr[0] : null };
}

/**
 * Range value canonicalization: `[null, null] → null`. Partial bounds preserved.
 * Plan Section "Core value shape".
 */
export function canonicalizeValue(item: FilterItemConfig, value: unknown): unknown {
  if (item.type !== 'DATE_RANGE' && item.type !== 'NUMERIC_RANGE') return value;
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) && value.length === 2 && value[0] == null && value[1] == null) {
    return null;
  }
  return value;
}

// ---------- Actions ----------

export type Action =
  | {
      type: 'CASCADE_OPEN';
      source: FilterChangeSource;
      trigger?: string;
      operation?: FilterBarOperation;
    }
  | { type: 'CASCADE_CLOSE' }
  | {
      type: 'FIELD_SET_VALUE';
      name: string;
      value: unknown;
      userTouched?: boolean;
    }
  | {
      type: 'FIELD_OPTIONS_LOADING';
      name: string;
      requestId: string;
    }
  | {
      type: 'FIELD_OPTIONS_RESOLVED';
      name: string;
      requestId: string;
      options: SelectOptionType[];
    }
  | {
      type: 'FIELD_OPTIONS_FAILED';
      name: string;
      requestId: string;
      error: string;
    }
  | {
      type: 'FIELD_PHASE';
      name: string;
      phase: FilterFieldPhase;
    }
  | {
      type: 'FIELD_ERROR';
      name: string;
      error: string;
    }
  | {
      type: 'FIELD_RESET';
      name: string;
    }
  | {
      type: 'SET_VISIBLE';
      visibleFields: string[];
    }
  | {
      type: 'ADD_FIELD';
      name: string;
    }
  | {
      type: 'REMOVE_FIELD';
      name: string;
    }
  | {
      type: 'RECONCILE_ITEMS';
      items: FilterItemConfig[];
    }
  | { type: 'SET_INITIALIZED' }
  | {
      type: 'PARTIAL_ERROR_ADD';
      name: string;
    };

// ---------- Helpers ----------

export function initialFieldState(): FieldState {
  return {
    phase: 'IDLE',
    options: [],
    value: undefined,
    userTouched: false,
    error: null,
    requestId: null,
  };
}

function buildItemsIndex(items: FilterItemConfig[]) {
  const byName: Record<string, FilterItemConfig> = {};
  const order: string[] = [];
  for (const item of items) {
    byName[item.name] = item;
    order.push(item.name);
  }
  return { byName, order };
}

export function initialState(items: FilterItemConfig[]): State {
  const { byName, order } = buildItemsIndex(items);
  const fields: Record<string, FieldState> = {};
  for (const item of items) {
    fields[item.name] = initialFieldState();
  }
  return {
    fields,
    itemsByName: byName,
    itemsOrder: order,
    visibleFields: [],
    transactionOpen: false,
    transactionSource: 'user',
    transactionTrigger: undefined,
    transactionOperation: undefined,
    prevValuesAtTxnStart: {},
    partialErrors: [],
    initialized: false,
    closedTxnCount: 0,
    initEmitCount: 0,
  };
}

export function currentValues(state: State): FilterValues {
  const out: FilterValues = {};
  for (const name of state.itemsOrder) {
    if (state.fields[name]) {
      out[name] = state.fields[name].value;
    }
  }
  return out;
}

// ---------- Reducer ----------

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'CASCADE_OPEN': {
      if (state.transactionOpen) return state;
      return {
        ...state,
        transactionOpen: true,
        transactionSource: action.source,
        transactionTrigger: action.trigger,
        transactionOperation: action.operation,
        prevValuesAtTxnStart: currentValues(state),
        partialErrors: [],
      };
    }

    case 'CASCADE_CLOSE': {
      if (!state.transactionOpen) return state;
      return {
        ...state,
        transactionOpen: false,
        closedTxnCount: state.closedTxnCount + 1,
      };
    }

    case 'FIELD_SET_VALUE': {
      const item = state.itemsByName[action.name];
      if (!item) return state;
      const value = canonicalizeValue(item, action.value);
      const prior = state.fields[action.name] ?? initialFieldState();
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            value,
            userTouched: action.userTouched ?? prior.userTouched,
          },
        },
      };
    }

    case 'FIELD_OPTIONS_LOADING': {
      const prior = state.fields[action.name] ?? initialFieldState();
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            phase: 'LOADING_OPTIONS',
            requestId: action.requestId,
            error: null,
          },
        },
      };
    }

    case 'FIELD_OPTIONS_RESOLVED': {
      const prior = state.fields[action.name];
      if (!prior) return state;
      // Stale check — discard if requestId mismatch
      if (prior.requestId !== action.requestId) return state;
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            phase: 'READY',
            options: action.options,
            error: null,
          },
        },
      };
    }

    case 'FIELD_OPTIONS_FAILED': {
      const prior = state.fields[action.name];
      if (!prior) return state;
      if (prior.requestId !== action.requestId) return state;
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            phase: 'ERROR',
            error: action.error,
          },
        },
        partialErrors: state.partialErrors.includes(action.name)
          ? state.partialErrors
          : [...state.partialErrors, action.name],
      };
    }

    case 'FIELD_PHASE': {
      const prior = state.fields[action.name] ?? initialFieldState();
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: { ...prior, phase: action.phase },
        },
      };
    }

    case 'FIELD_ERROR': {
      const prior = state.fields[action.name];
      if (!prior) return state;
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            phase: 'ERROR',
            error: action.error,
          },
        },
        partialErrors: state.partialErrors.includes(action.name)
          ? state.partialErrors
          : [...state.partialErrors, action.name],
      };
    }

    case 'FIELD_RESET': {
      const item = state.itemsByName[action.name];
      if (!item) return state;
      const prior = state.fields[action.name] ?? initialFieldState();
      // Sync part: write `defaultValue` if set, else null (orchestrator runs
      // getDefaultValue post-dispatch to finish the baseline restore).
      const target = canonicalizeValue(item, baselineTarget(item));
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.name]: { ...prior, value: target, userTouched: false },
        },
      };
    }

    case 'SET_VISIBLE':
      return { ...state, visibleFields: [...action.visibleFields] };

    case 'ADD_FIELD': {
      if (state.visibleFields.includes(action.name)) return state;
      return {
        ...state,
        visibleFields: [...state.visibleFields, action.name],
      };
    }

    case 'REMOVE_FIELD': {
      const item = state.itemsByName[action.name];
      if (!item) return state;
      const prior = state.fields[action.name] ?? initialFieldState();
      return {
        ...state,
        visibleFields: state.visibleFields.filter((n) => n !== action.name),
        fields: {
          ...state.fields,
          [action.name]: {
            ...prior,
            value: canonicalEmpty(item),
            userTouched: false,
          },
        },
      };
    }

    case 'RECONCILE_ITEMS': {
      const { byName: nextByName, order: nextOrder } = buildItemsIndex(action.items);
      const nextFields: Record<string, FieldState> = {};
      for (const name of nextOrder) {
        const prior = state.fields[name];
        const prevItem = state.itemsByName[name];
        const nextItem = nextByName[name];
        // Fresh state is seeded with the canonical empty (never `undefined`):
        // the orchestrator's default resolution only writes non-null defaults,
        // so an unseeded field would leak `undefined` into values/persistence.
        if (!prior || !prevItem || prevItem.type === nextItem.type) {
          nextFields[name] = prior
            ?? { ...initialFieldState(), value: canonicalEmpty(nextItem) };
          continue;
        }
        const conversion = convertOptionBackedValue(prevItem.type, nextItem.type, prior.value);
        if (conversion.converted) {
          // Same option domain: keep loaded options and userTouched — the
          // orchestrator re-validates the converted value right after.
          nextFields[name] = { ...prior, value: conversion.value };
        } else {
          // Plan: "different type → treat as remove + add". The orchestrator
          // re-inits the field through the full default chain.
          nextFields[name] = { ...initialFieldState(), value: canonicalEmpty(nextItem) };
        }
      }
      // Plan: a new `name` "joins the bar at its position" — kept fields keep
      // their visibility, added names become visible (mount defaults to
      // all-visible, so reconcile-adds match). `initialVisibleFields` may
      // pre-declare a not-yet-known name, hence the duplicate guard.
      const keptVisible = state.visibleFields.filter((n) => nextByName[n]);
      const visibleSet = new Set(keptVisible);
      for (const name of nextOrder) {
        if (!state.itemsByName[name] && !visibleSet.has(name)) keptVisible.push(name);
      }
      return {
        ...state,
        itemsByName: nextByName,
        itemsOrder: nextOrder,
        fields: nextFields,
        visibleFields: keptVisible,
      };
    }

    case 'SET_INITIALIZED':
      if (state.initialized) return state;
      return { ...state, initialized: true, initEmitCount: state.initEmitCount + 1 };

    case 'PARTIAL_ERROR_ADD': {
      if (state.partialErrors.includes(action.name)) return state;
      return { ...state, partialErrors: [...state.partialErrors, action.name] };
    }

    default:
      return state;
  }
}
